import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserProfile, ToastType } from '../types';
import { getRoddoUiTokens } from '../constants/theme';
import { createSupportTicket, type CreateSupportTicketInput } from '../services/support';
import { requestAccountDeletion } from '../services/accountDeletionRequests';
import {
  formatRoddoSupportDiagnostics,
  getRoddoSupportDiagnostics,
  supportDeviceInfo,
  supportVersionLabel,
  type RoddoSupportDiagnostics,
} from '../services/supportDiagnostics';

type SupportViewProps = {
  userProfile?: UserProfile | null;
  currentUserProfile?: UserProfile | null;
  addToast?: (message: string, type?: ToastType) => void;
  onBack?: () => void;
  isDarkMode?: boolean;
};

type SupportCategory = NonNullable<CreateSupportTicketInput['category']>;

const categories: SupportCategory[] = ['bug', 'account', 'billing', 'feature', 'other'];

const categoryMeta: Record<SupportCategory, { label: string; icon: string; hint: string }> = {
  bug: { label: 'Bug', icon: '🐛', hint: 'Something is not working right.' },
  account: { label: 'Account', icon: '👤', hint: 'Login, profile, or settings help.' },
  billing: { label: 'Billing', icon: '🧾', hint: 'Payment, receipt, or marketplace questions.' },
  feature: { label: 'Feature', icon: '✨', hint: 'Ideas to make Roddo better.' },
  other: { label: 'Other', icon: '🎣', hint: 'Anything else we can help with.' },
};

const inputBase =
  'roddo-mobile-form w-full rounded-[12px] px-4 py-3 text-sm font-semibold outline-none transition placeholder:opacity-70';

const SUPPORT_MIN_DETAIL_CHARS = 24;
const SUPPORT_MIN_SUBJECT_CHARS = 4;

const SupportView: React.FC<SupportViewProps> = ({
  userProfile,
  currentUserProfile,
  addToast,
  onBack,
  isDarkMode = false,
}) => {
  const { t } = useTranslation();
  const profile = (currentUserProfile ?? userProfile ?? null) as any;
  const ui = getRoddoUiTokens(isDarkMode);

  const [category, setCategory] = useState<SupportCategory>('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState(String(profile?.email || '').trim());
  const [submitting, setSubmitting] = useState(false);
  const [accountDataOpen, setAccountDataOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionSubmitting, setDeletionSubmitting] = useState(false);
  const [diagnostics, setDiagnostics] = useState<RoddoSupportDiagnostics | null>(null);
  const submitLockRef = useRef(false);

  const subjectLength = subject.trim().length;
  const messageLength = message.trim().length;
  const needsMoreSubject = subjectLength > 0 && subjectLength < SUPPORT_MIN_SUBJECT_CHARS;
  const needsMoreDetail = messageLength > 0 && messageLength < SUPPORT_MIN_DETAIL_CHARS;

  useEffect(() => {
    setContactEmail(String(profile?.email || '').trim());
  }, [profile?.email]);

  useEffect(() => {
    let alive = true;
    void getRoddoSupportDiagnostics().then((next) => {
      if (alive) setDiagnostics(next);
    }).catch(() => {
      if (alive) setDiagnostics(null);
    });
    return () => {
      alive = false;
    };
  }, [profile?.uid]);

  const copyDiagnostics = async () => {
    if (!diagnostics) return;
    try {
      await navigator.clipboard.writeText(formatRoddoSupportDiagnostics(diagnostics));
      addToast?.(t('support.diagnosticsCopied', 'Diagnostics copied.'), 'success');
    } catch {
      addToast?.(t('support.diagnosticsCopyFailed', 'Could not copy diagnostics on this device.'), 'warning');
    }
  };

  const cardStyle = useMemo<React.CSSProperties>(
    () => ({
      background: isDarkMode ? '#101827' : '#FFFCF7',
      border: `1px solid ${ui.border}`,
      boxShadow: isDarkMode
        ? '0 5px 14px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.025)'
        : '0 8px 20px rgba(89,65,37,0.07), inset 0 1px 0 rgba(255,255,255,0.62)',
      color: ui.textPrimary,
    }),
    [isDarkMode, ui],
  );

  const softCardStyle = useMemo<React.CSSProperties>(
    () => ({
      background: isDarkMode ? '#0F1828' : '#F2EBE1',
      border: `1px solid ${ui.border}`,
      boxShadow: 'none',
      color: ui.textPrimary,
    }),
    [isDarkMode, ui],
  );

  const fieldStyle = useMemo<React.CSSProperties>(
    () => ({
      background: isDarkMode ? '#0C1423' : '#FFFCF7',
      border: `1px solid ${ui.borderStrong}`,
      color: ui.textPrimary,
      boxShadow: isDarkMode ? 'inset 0 1px 0 rgba(255,255,255,0.035)' : 'inset 0 1px 0 rgba(255,255,255,0.70)',
    }),
    [isDarkMode, ui],
  );

  const buttonStyle = useMemo<React.CSSProperties>(
    () => ({
      background: submitting
        ? isDarkMode
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(0,0,0,0.08)'
        : isDarkMode ? '#8A70F2' : '#2F80ED',
      color: submitting ? ui.textMuted : '#FFFFFF',
      boxShadow: submitting
        ? 'none'
        : isDarkMode
          ? '0 5px 14px rgba(138,112,242,0.15)'
          : '0 5px 14px rgba(47,128,237,0.16)',
    }),
    [isDarkMode, submitting, ui.textMuted],
  );

  const handleSubmit = async () => {
    if (submitLockRef.current || submitting) return;

    const cleanSubject = subject.trim();
    const cleanMessage = message.trim();
    const cleanEmail = contactEmail.trim();

    if (!cleanSubject) {
      addToast?.(t('support.addSubject', 'Please add a short subject so we know where to start.'), 'warning');
      return;
    }

    if (cleanSubject.length < SUPPORT_MIN_SUBJECT_CHARS) {
      addToast?.(t('support.subjectTooShort', 'Add a little more to the subject.'), 'warning');
      return;
    }

    if (!cleanMessage) {
      addToast?.(t('support.describeIssue', 'Tell us what happened so we can help.'), 'warning');
      return;
    }

    if (cleanMessage.length < SUPPORT_MIN_DETAIL_CHARS) {
      addToast?.(
        t('support.moreDetailNeeded', 'Add a little more detail before sending.'),
        'warning',
      );
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);

    try {
      const payload: CreateSupportTicketInput = {
        category,
        subject: cleanSubject,
        message: cleanMessage,
        contactEmail: cleanEmail || undefined,
        userId: profile?.uid || undefined,
        userName: profile?.displayName || profile?.name || undefined,
        deviceInfo: diagnostics ? supportDeviceInfo(diagnostics) : undefined,
        appVersion: diagnostics ? supportVersionLabel(diagnostics) : undefined,
      };

      await createSupportTicket(payload);

      addToast?.(t('support.ticketSent', 'Support ticket sent.'), 'success');
      setSubject('');
      setMessage('');
      setCategory('bug');
    } catch (err: any) {
      console.error('Failed to create support ticket:', err);
      const rawMessage = String(err?.message || '').trim();
      const friendlyMessage = /detail/i.test(rawMessage)
        ? t('support.moreDetailNeeded', 'Add a little more detail before sending.')
        : t('support.ticketError', 'Could not send the ticket yet. Please try again in a moment.');
      addToast?.(friendlyMessage, 'warning');
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const submitAccountDeletionRequest = async () => {
    const uid = String(profile?.uid || '').trim();
    if (!uid || deletionSubmitting) {
      if (!uid) addToast?.(t('support.deletionMissingUser', 'Could not submit deletion request: missing user id.'), 'error');
      return;
    }

    const confirmed = window.confirm(
      t(
        'support.deletionConfirm',
        'Submit an account deletion request? Roddo support/admin will review and process deletion subject to legal, safety, and anti-abuse requirements.',
      ),
    );
    if (!confirmed) return;

    setDeletionSubmitting(true);
    try {
      const result = await requestAccountDeletion({
        uid,
        email: profile?.email ?? profile?.contactEmail ?? null,
        displayName: profile?.displayName || profile?.name || 'Angler',
        reason: deletionReason.trim(),
      });
      const scheduledAt = Number(result?.scheduledDeletionAt || 0);
      const statusCopy = result?.alreadyExists
        ? t('support.deletionAlreadyScheduled', 'Your deletion request is already on file.')
        : scheduledAt > 0
          ? t('support.deletionScheduled', 'Account deletion request submitted and scheduled.')
          : t('support.deletionSubmitted', 'Account deletion request submitted.');
      addToast?.(statusCopy, 'success');
      setDeletionReason('');
      setAccountDataOpen(false);
    } catch (err: any) {
      console.error('[SupportView] account deletion request failed:', err);
      addToast?.(t('support.deletionError', 'Could not submit deletion request. Please try again.'), 'error');
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const activeMeta = categoryMeta[category];
  const activeLabel = t(`support.categories.${category}.label`, activeMeta.label);

  return (
    <div
      className="mx-auto w-full max-w-3xl space-y-6 px-0 pb-6 roddo-support-polish lg:py-2"
      data-roddo-polish-lane="2"
      data-roddo-theme={isDarkMode ? 'dark' : 'light'}
    >
      <section className="px-1 pt-1">
        <h2 className="text-[20px] font-black leading-tight" style={{ color: ui.textPrimary }}>
          {t('support.title', 'Support')}
        </h2>
        <p className="mt-1.5 max-w-xl text-[12px] font-semibold leading-relaxed" style={{ color: ui.textSecondary }}>
          {t('support.subtitle', 'Tell us what happened and the Roddo crew will take a look.')}
        </p>
      </section>

      <section className="space-y-2">
        <label className="block">
          <div className="mb-2 text-[12px] font-black" style={{ color: ui.textSecondary }}>
            {t('support.issueCategory', 'Issue category')}
          </div>

          <div className="relative">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as SupportCategory)}
              className={`${inputBase} appearance-none pr-11`}
              style={fieldStyle}
              aria-label={t('support.issueCategory', 'Issue category')}
            >
              {categories.map((item) => {
                const meta = categoryMeta[item];
                const label = t(`support.categories.${item}.label`, meta.label);
                return (
                  <option key={item} value={item}>
                    {meta.icon} {label}
                  </option>
                );
              })}
            </select>

            <span
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[12px] font-black"
              style={{ color: ui.textMuted }}
              aria-hidden="true"
            >
              ▾
            </span>
          </div>

          <div className="mt-2 flex items-center gap-2 text-[11px] font-semibold" style={{ color: ui.textMuted }}>
            <span>{activeMeta.icon}</span>
            <span>{t(`support.categories.${category}.hint`, activeMeta.hint)}</span>
          </div>
        </label>
      </section>

      <section className="rounded-[16px] p-4 sm:p-5" style={cardStyle}>
        <div className="mb-5">
          <div className="text-[14px] font-black" style={{ color: ui.textPrimary }}>
            {t('support.ticketDetails', 'Ticket details')}
          </div>
        </div>

        <div className="space-y-5">
          <label className="block">
            <div className="mb-2.5 text-[12px] font-black" style={{ color: ui.textSecondary }}>
              {t('support.subject', 'Subject')}
            </div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={inputBase}
              style={fieldStyle}
              placeholder={t('support.subjectPlaceholder', 'Map button did not work')}
              maxLength={120}
            />
            {needsMoreSubject ? (
              <div className="mt-2 text-[10px] font-bold" style={{ color: '#B45309' }}>
                {t('support.subjectHelperMore', 'Add a few more words so we can route this correctly.')}
              </div>
            ) : null}
          </label>

          <label className="block">
            <div className="mb-2.5 text-[12px] font-black" style={{ color: ui.textSecondary }}>
              {t('support.contactEmail', 'Contact email')}
            </div>
            <input
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className={inputBase}
              style={fieldStyle}
              placeholder="you@example.com"
              maxLength={160}
            />
          </label>

          <label className="block">
            <div className="mb-2.5 text-[12px] font-black" style={{ color: ui.textSecondary }}>
              {t('support.whatHappened', 'What happened?')}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`${inputBase} min-h-[138px] resize-y`}
              style={fieldStyle}
              placeholder={t('support.messagePlaceholder', 'Tell us what happened, what screen you were on, and what you expected to happen.')}
              maxLength={4000}
            />
            <div
              className="mt-2 flex items-center justify-between gap-3 text-[10px] font-semibold"
              style={{ color: needsMoreDetail ? '#B45309' : ui.textMuted }}
            >
              <span>
                {needsMoreDetail
                  ? t('support.detailHelperMore', 'A little more detail helps the crew fix it faster.')
                  : t('support.detailHelper', 'Screen, steps, and what you expected are usually enough.')}
              </span>
              <span className="shrink-0">{Math.min(messageLength, SUPPORT_MIN_DETAIL_CHARS)}/{SUPPORT_MIN_DETAIL_CHARS}</span>
            </div>
          </label>
        </div>
      </section>

      <section className="rounded-[16px] p-4 sm:p-5" style={softCardStyle}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textMuted }}>
              {t('support.diagnostics', 'Diagnostics')}
            </div>
            <div className="mt-1 text-sm font-black" style={{ color: ui.textPrimary }}>
              {diagnostics ? `${diagnostics.platform} · ${supportVersionLabel(diagnostics)}` : t('support.diagnosticsLoading', 'Loading app details…')}
            </div>
            <div className="mt-1 text-[11px] font-semibold leading-4" style={{ color: ui.textMuted }}>
              {diagnostics
                ? `${diagnostics.accountType} · auth ${diagnostics.authBridge}`
                : t('support.diagnosticsSafe', 'Roddo only includes coarse app/session diagnostics.')}
            </div>
            {diagnostics ? (
              <div className="mt-2 break-all font-mono text-[10px] font-bold" style={{ color: ui.textSecondary }}>
                {diagnostics.diagnosticId}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void copyDiagnostics()}
            disabled={!diagnostics}
            className="shrink-0 rounded-[12px] px-3 py-2 text-xs font-black transition active:translate-y-[1px] disabled:opacity-50"
            style={fieldStyle}
          >
            {t('support.copyDiagnostics', 'Copy')}
          </button>
        </div>
        <p className="mt-3 text-[10px] font-semibold leading-4" style={{ color: ui.textMuted }}>
          {t('support.diagnosticsPrivacy', 'No password, auth token, exact location, catch data, or raw device fingerprint is included.')}
        </p>
      </section>

      <section className="rounded-[16px] p-4 sm:p-5" style={softCardStyle}>
        <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textMuted }}>
          {t('support.copyrightDmca', 'Copyright / DMCA')}
        </div>
        <div className="mt-1 text-sm font-black" style={{ color: ui.textPrimary }}>
          {t('support.copyrightAgent', 'Copyright Agent')}
        </div>
        <p className="mt-1 text-[11px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
          {t(
            'support.copyrightDmcaCopy',
            'Copyright infringement notices may be sent to Roddo Technology LLC at legal@roddo.app.',
          )}
        </p>
        <a
          href="mailto:legal@roddo.app"
          className="mt-3 inline-flex rounded-[12px] px-4 py-2.5 text-xs font-black transition hover:-translate-y-0.5 active:translate-y-[1px]"
          style={{
            background: isDarkMode ? '#8A70F2' : '#2F80ED',
            color: '#FFFFFF',
          }}
        >
          {t('support.emailLegal', 'Email legal@roddo.app')}
        </a>
      </section>

      <div className="pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full rounded-[13px] px-5 py-3 text-sm font-black transition hover:-translate-y-0.5 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-70"
          style={buttonStyle}
        >
          {submitting ? t('support.sending', 'Sending…') : t('support.sendTicket', 'Send support ticket')}
        </button>
      <section className="pt-7">
        <div className="border-t pt-5" style={{ borderColor: ui.border }}>
          <button
            type="button"
            onClick={() => setAccountDataOpen((value) => !value)}
            className="flex w-full items-center justify-between gap-3 rounded-[14px] px-3 py-3 text-left transition active:translate-y-[1px]"
            style={softCardStyle}
            aria-expanded={accountDataOpen}
          >
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textMuted }}>
                {t('support.accountData', 'Account & data')}
              </div>
              <div className="mt-1 text-sm font-black" style={{ color: ui.textPrimary }}>
                {t('support.accountDeletion', 'Account deletion')}
              </div>
              <div className="mt-1 text-[11px] font-semibold leading-4" style={{ color: ui.textMuted }}>
                {t('support.accountDeletionHint', 'Sensitive account actions stay tucked away here in Support.')}
              </div>
            </div>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border text-sm font-black" style={{ borderColor: ui.borderStrong, color: ui.textMuted }}>
              {accountDataOpen ? '−' : '+'}
            </span>
          </button>

          {accountDataOpen ? (
            <div className="mt-3 rounded-[14px] p-4" style={{
              background: isDarkMode ? 'rgba(239,106,115,0.055)' : '#FFF7F7',
              border: `1px solid ${isDarkMode ? 'rgba(239,106,115,0.20)' : 'rgba(190,24,93,0.14)'}`,
            }}>
              <div className="text-sm font-black" style={{ color: isDarkMode ? '#F6C6CA' : '#8A2D39' }}>
                {t('support.requestDeletion', 'Request account deletion')}
              </div>
              <p className="mt-1 text-xs leading-5" style={{ color: ui.textSecondary }}>
                {t('support.requestDeletionCopy', 'This submits a review request to Roddo support. Some records may be retained where required for legal, safety, anti-abuse, or operational reasons.')}
              </p>
              <textarea
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                disabled={deletionSubmitting}
                placeholder={t('support.deletionReasonPlaceholder', 'Optional reason for the deletion request')}
                className={`${inputBase} mt-3 min-h-[82px] resize-none`}
                style={fieldStyle}
              />
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[11px] font-semibold leading-4" style={{ color: ui.textMuted }}>
                  {t('support.deletionConfirmNote', 'A confirmation appears before the request is submitted.')}
                </p>
                <button
                  type="button"
                  onClick={submitAccountDeletionRequest}
                  disabled={deletionSubmitting}
                  className="rounded-[12px] px-4 py-2.5 text-xs font-black transition active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    background: isDarkMode ? 'rgba(239,106,115,0.12)' : '#FDECEC',
                    border: `1px solid ${isDarkMode ? 'rgba(239,106,115,0.30)' : 'rgba(190,24,93,0.18)'}`,
                    color: isDarkMode ? '#FFD6D9' : '#8A2D39',
                  }}
                >
                  {deletionSubmitting ? t('support.submitting', 'Submitting…') : t('support.requestDeletionButton', 'Request deletion')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
      </div>
    </div>
  );
};

export default SupportView;
